/**
 * Validation utilities for Design Detail module
 * Contains all file validation logic for images and documents
 */

/**
 * Validate image file upload with comprehensive security checks
 * Ensures only valid image files are uploaded
 *
 * @param {File} file - File object to validate
 * @param {Function} t - Translation function
 * @param {Function} toasterMsg - Toast message function
 * @returns {boolean} - True if valid, false otherwise
 */
import { MAX_IMAGE_SIZE_MB } from './helpers';

export const NON_STANDARD_EXTENSIONS = ['.dxf', '.rul', '.wds'];
export const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.tiff', '.tif', '.svg'];
export const PREVIEWABLE_EXTENSIONS = [...IMAGE_EXTENSIONS, '.ai', '.pdf'];

const getExtension = (filename) => {
  const parts = filename.toLowerCase().split('.');
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
};

export const validateImageFile = (file, t, toasterMsg) => {
  const fileSizeMB = file.size / 1024 / 1024;

  if (fileSizeMB > MAX_IMAGE_SIZE_MB) {
    toasterMsg(t('DESIGN_DETAILS.IMAGE_TOO_LARGE') || `Image size (${fileSizeMB.toFixed(2)}MB) exceeds ${MAX_IMAGE_SIZE_MB}MB limit`, 'danger');
    return false;
  }

  const imageFileName = file.name.toLowerCase();
  const allowedExtensions = ['.jpg', '.jpeg', '.png'];
  const hasValidExtension = allowedExtensions.some((ext) => getExtension(imageFileName) === ext);

  if (!hasValidExtension) {
    toasterMsg(t('DESIGN_DETAILS.INVALID_IMAGE_EXTENSION') || 'Invalid file extension. Only .jpg, .jpeg, .png, .gif, and .webp are allowed.', 'danger');
    return false;
  }

  if (file.name.includes('../') || file.name.includes('..\\')) {
    toasterMsg(t('DESIGN_DETAILS.INVALID_FILE_NAME') || 'Invalid file name detected.', 'danger');
    return false;
  }

  return true;
};

/**
 * Validate file upload with comprehensive security checks
 * Prevents malicious file uploads by validating MIME type, size, and format
 *
 * @param {File} file - File object to validate
 * @param {Function} t - Translation function
 * @returns {Object} - {valid: boolean, errors: string[]}
 */
export const validateFileUpload = (file, t) => {
  const validationErrors = [];

  if (file.name.includes('../') || file.name.includes('..\\')) {
    validationErrors.push(t('TAKING_DATES.INVALID_FILE_NAME') || 'Invalid file name detected.');
    return { valid: false, errors: validationErrors };
  }

  const docFileName = file.name.toLowerCase();

  if (NON_STANDARD_EXTENSIONS.some((ext) => getExtension(docFileName) === ext)) {
    return { valid: true, errors: [] };
  }

  /** 4. Raster images — validate by MIME type and extension together. */
  const imageMimeTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
  const isImageExtension = imageExtensions.some((ext) => getExtension(docFileName) === ext);
  if (isImageExtension && imageMimeTypes.includes(file.type)) {
    return { valid: true, errors: [] };
  }

  /** 5. MIME type check for standard formats */
  const allowedMimeTypes = {
    pdf: ['application/pdf'],
    ai: ['application/postscript', 'application/illustrator'],
  };
  const allAllowedTypes = [...allowedMimeTypes.pdf, ...allowedMimeTypes.ai];
  if (!allAllowedTypes.includes(file.type)) {
    validationErrors.push(t('TAKING_DATES.INVALID_FILE_TYPE') || `Invalid file type "${file.type}". Only PDF, AI, DXF, RUL, WDS and image files are allowed.`);
    return { valid: false, errors: validationErrors };
  }

  /** 6. Extension check for standard formats */
  const allowedExtensions = ['.pdf', '.ai'];
  const hasValidExtension = allowedExtensions.some((ext) => getExtension(docFileName) === ext);
  if (!hasValidExtension) {
    validationErrors.push(t('TAKING_DATES.INVALID_FILE_EXTENSION') || 'Invalid file extension. Only .pdf, .ai, .dxf, .rul, .wds and image files are allowed.');
    return { valid: false, errors: validationErrors };
  }

  return { valid: true, errors: [] };
};

/**
 * Convert file to base64
 * @param {File} file - File to convert
 * @returns {Promise<string>} - Base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};
